import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
   Select,
   SelectContent,
   SelectItem,
   SelectTrigger,
   SelectValue,
} from "@/components/ui/select";
import {
   Table,
   TableBody,
   TableCell,
   TableHead,
   TableHeader,
   TableRow,
} from "@/components/ui/table";
import {
   Tooltip,
   TooltipContent,
   TooltipProvider,
   TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { gql, useMutation, useQuery } from '@apollo/client';
import { format } from 'date-fns';
import { Bell, FileX2, Loader2, RefreshCcw, Search } from "lucide-react";
import { useMemo, useState } from 'react';
import { toast } from "sonner";

const GET_EXPIRING_PERMITS = gql`
   query GetExpiringPermits(
      $permitType: String
      $expiryTimeframe: Int
      $status: String
      $sortBy: String
      $sortOrder: String
   ) {
      getExpiringPermits(
         permitType: $permitType
         expiryTimeframe: $expiryTimeframe
         status: $status
         sortBy: $sortBy
         sortOrder: $sortOrder
      ) {
         id
         applicationNumber
         applicationType
         status
         applicantDetails {
            name
            contact
         }
         certificateDetails {
            expiryDate
            daysLeft
         }
      }
   }
`;

const SEND_EXPIRY_NOTIFICATION = gql`
   mutation SendPermitExpiryNotification($permitId: ID!, $type: String!) {
      sendPermitExpiryNotification(permitId: $permitId, type: $type)
   }
`;

const PERMIT_TYPES = [
   { value: "all", label: "All Types" },
   { value: "Chainsaw Registration", label: "Chainsaw Registration" },
   { value: "Certificate of Verification", label: "Certificate of Verification" },
   { value: "Private Tree Plantation Registration", label: "Private Tree Plantation Registration" },
   { value: "Public Land Tree Cutting Permit", label: "Public Land Tree Cutting Permit" },
   { value: "Private Land Timber Permit", label: "Private Land Timber Permit" },
   { value: "Tree Cutting and/or Earth Balling Permit", label: "Tree Cutting and/or Earth Balling Permit" }
];

const TIMEFRAME_OPTIONS = [
   { value: "30", label: "30 Days" },
   { value: "60", label: "60 Days" },
   { value: "90", label: "90 Days" }
];

const SORT_OPTIONS = [
   { value: "daysLeft", label: "Days Left" },
   { value: "applicationNumber", label: "Application Number" },
   { value: "applicantName", label: "Applicant Name" }
];

const SORT_ORDER_OPTIONS = [
   { value: "asc", label: "Ascending" },
   { value: "desc", label: "Descending" }
];

const STATUS_OPTIONS = [
   { value: "all", label: "All Statuses" },
   { value: "Released", label: "Active" },
   { value: "Expired", label: "Expired" },
   { value: "ForRenewal", label: "For Renewal" }
];

const customBadgeVariants = {
   orange: "bg-orange-100 text-orange-800 hover:bg-orange-100/80",
   yellow: "bg-yellow-100 text-yellow-800 hover:bg-yellow-100/80",
   blue: "bg-blue-100 text-blue-800 hover:bg-blue-100/80",
};

const ExpiryLegend = () => (
   <div className="flex gap-4 mb-4 p-4 bg-white dark:bg-gray-800/50 rounded-lg border dark:border-gray-700">
      <h3 className="font-semibold mr-2 dark:text-gray-100">Expiry Timeline:</h3>
      <div className="flex items-center gap-6">
         <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800" />
            <span className="text-sm dark:text-gray-300">1 day or less</span>
         </div>
         <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800" />
            <span className="text-sm dark:text-gray-300">7 days or less</span>
         </div>
         <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800" />
            <span className="text-sm dark:text-gray-300">14 days or less</span>
         </div>
         <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800" />
            <span className="text-sm dark:text-gray-300">30 days or less</span>
         </div>
      </div>
   </div>
);

const EmptyState = ({ searchTerm, onReset }) => (
   <div className="flex flex-col items-center justify-center py-12 px-4">
      <FileX2 className="h-16 w-16 text-gray-400 dark:text-gray-600 mb-4" />
      <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
         No permits found
      </h3>
      <p className="text-gray-500 dark:text-gray-400 text-center max-w-sm mb-4">
         {searchTerm
            ? `No permits match the search term "${searchTerm}"`
            : "No permits found with the current filters"}
      </p>
      <Button
         variant="outline"
         onClick={onReset}
         className="dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
      >
         <RefreshCcw className="h-4 w-4 mr-2" />
         Reset Filters
      </Button>
   </div>
);

const ExpiringPermitsPage = () => {
   const [filters, setFilters] = useState({
      permitType: "all",
      expiryTimeframe: 90,
      status: "all",
      sortBy: "daysLeft",
      sortOrder: "asc"
   });

   const [searchTerm, setSearchTerm] = useState('');

   const isChrome = useMemo(() => {
      const userAgent = window.navigator.userAgent.toLowerCase();
      const isBrave = navigator.brave !== undefined;
      return userAgent.includes('chrome') && !userAgent.includes('edg') && !isBrave;
   }, []);

   const { loading, error, data, refetch } = useQuery(GET_EXPIRING_PERMITS, {
      variables: {
         ...filters,
         permitType: filters.permitType === "all" ? null : filters.permitType
      },
      fetchPolicy: 'network-only'
   });

   const [sendNotification] = useMutation(SEND_EXPIRY_NOTIFICATION, {
      context: {
         headers: {
            authorization: `Bearer ${localStorage.getItem('token')}`
         }
      }
   });

   const [isRefreshing, setIsRefreshing] = useState(false);

   const handleResetFilters = () => {
      setFilters({
         permitType: "all",
         expiryTimeframe: 90,
         status: "all",
         sortBy: "daysLeft",
         sortOrder: "asc"
      });
      setSearchTerm('');
   };

   const handleRefresh = async () => {
      setIsRefreshing(true);
      try {
         await refetch();
         toast.success('Data refreshed successfully');
      } catch (error) {
         toast.error('Failed to refresh data');
      } finally {
         setIsRefreshing(false);
      }
   };

   const getRowColorClass = (daysLeft) => {
      if (daysLeft <= 1) return "bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/30";
      if (daysLeft <= 7) return "bg-orange-50 dark:bg-orange-900/20 hover:bg-orange-100 dark:hover:bg-orange-900/30";
      if (daysLeft <= 14) return "bg-yellow-50 dark:bg-yellow-900/20 hover:bg-yellow-100 dark:hover:bg-yellow-900/30";
      if (daysLeft <= 30) return "bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30";
      return "hover:bg-gray-100 dark:hover:bg-gray-800/50";
   };

   const getStatusBadgeVariant = (daysLeft, status) => {
      if (status === 'Expired') return "destructive";
      if (daysLeft <= 1) return "destructive";
      if (daysLeft <= 7) return "orange";
      if (daysLeft <= 14) return "yellow";
      if (daysLeft <= 30) return "blue";
      return "secondary";
   };

   const handleFilterChange = (key, value) => {
      setFilters(prev => ({ ...prev, [key]: value }));
   };

   const handleNotify = async (permit) => {
      try {
         const type = permit.status === 'Expired' ?
            'PERMIT_EXPIRED' : 'PERMIT_EXPIRING_SOON';

         await sendNotification({
            variables: {
               permitId: permit.id,
               type
            }
         });

         toast.success("Notification Sent", {
            description: `Successfully notified permit holder of ${permit.applicationNumber}`
         });
      } catch (error) {
         console.error('Error sending notification:', error);
         toast.error("Error", {
            description: "Failed to send notification. Please try again."
         });
      }
   };

   const filteredPermits = useMemo(() => {
      if (!data?.getExpiringPermits) return [];
      if (!searchTerm) return data.getExpiringPermits;

      const searchLower = searchTerm.toLowerCase();
      return data.getExpiringPermits.filter(permit =>
         permit.applicationNumber.toLowerCase().includes(searchLower) ||
         permit.applicantDetails.name.toLowerCase().includes(searchLower)
      );
   }, [data?.getExpiringPermits, searchTerm]);

   if (loading) return (
      <div className="flex items-center justify-center min-h-screen">
         <Loader2 className="h-8 w-8 animate-spin" />
      </div>
   );

   if (error) return (
      <div className="text-red-500 p-4">
         Error loading expiring permits: {error.message}
      </div>
   );

   const SelectComponent = ({ options, value, onChange, className = "w-[250px]" }) => {
      if (isChrome) {
         return (
            <select
               value={value}
               onChange={(e) => onChange(e.target.value)}
               className={`h-10 px-3 py-2 rounded-md border border-input bg-background text-sm ${className}`}
            >
               {options.map((option) => (
                  <option key={option.value} value={option.value}>
                     {option.label}
                  </option>
               ))}
            </select>
         );
      }

      return (
         <Select value={value} onValueChange={onChange}>
            <SelectTrigger className={className}>
               <SelectValue />
            </SelectTrigger>
            <SelectContent>
               {options.map(option => (
                  <SelectItem key={option.value} value={option.value}>
                     {option.label}
                  </SelectItem>
               ))}
            </SelectContent>
         </Select>
      );
   };

   return (
      <div className="min-h-screen bg-gradient-to-b from-green-50 to-white dark:from-gray-800 dark:to-gray-900 transition-colors duration-300 pt-20 pb-8 px-4 sm:px-6 lg:px-8">
         <div className="container mx-auto space-y-6 pt-2">
            <div className="bg-white dark:bg-black border dark:border-gray-800 rounded-lg shadow-sm overflow-hidden">
               <div className="p-6">
                  <div className="flex items-center justify-between mb-6">
                     <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                        Expiring Permits Management
                     </h1>
                     <Button
                        variant="outline"
                        size="sm"
                        onClick={handleRefresh}
                        disabled={isRefreshing}
                        className="dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
                     >
                        <RefreshCcw className={cn(
                           "h-4 w-4 mr-2",
                           isRefreshing && "animate-spin"
                        )} />
                        Refresh
                     </Button>
                  </div>

                  <div className="flex gap-4 mb-6">
                     <div className="relative w-[300px]">
                        <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                        <Input
                           placeholder="Search by application # or applicant"
                           className="pl-8 dark:bg-gray-800/50 dark:border-gray-700 dark:text-gray-100"
                           value={searchTerm}
                           onChange={(e) => setSearchTerm(e.target.value)}
                        />
                     </div>

                     <SelectComponent
                        options={PERMIT_TYPES}
                        value={filters.permitType}
                        onChange={(value) => handleFilterChange('permitType', value)}
                        className="w-[250px]"
                     />

                     <SelectComponent
                        options={TIMEFRAME_OPTIONS}
                        value={filters.expiryTimeframe.toString()}
                        onChange={(value) => handleFilterChange('expiryTimeframe', parseInt(value))}
                        className="w-[200px]"
                     />

                     <SelectComponent
                        options={SORT_OPTIONS}
                        value={filters.sortBy}
                        onChange={(value) => handleFilterChange('sortBy', value)}
                        className="w-[200px]"
                     />

                     <SelectComponent
                        options={SORT_ORDER_OPTIONS}
                        value={filters.sortOrder}
                        onChange={(value) => handleFilterChange('sortOrder', value)}
                        className="w-[150px]"
                     />

                     <SelectComponent
                        options={STATUS_OPTIONS}
                        value={filters.status}
                        onChange={(value) => handleFilterChange('status', value)}
                        className="w-[200px]"
                     />
                  </div>

                  <ExpiryLegend />

                  <div className="border dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800/50">
                     {loading ? (
                        <div className="flex items-center justify-center py-12">
                           <Loader2 className="h-8 w-8 animate-spin text-gray-400 dark:text-gray-600" />
                        </div>
                     ) : error ? (
                        <div className="flex flex-col items-center justify-center py-12 px-4">
                           <p className="text-red-500 dark:text-red-400 text-center">
                              Error loading permits: {error.message}
                           </p>
                           <Button
                              variant="outline"
                              onClick={handleRefresh}
                              className="mt-4 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
                           >
                              Try Again
                           </Button>
                        </div>
                     ) : filteredPermits.length === 0 ? (
                        <EmptyState
                           searchTerm={searchTerm}
                           onReset={handleResetFilters}
                        />
                     ) : (
                        <Table>
                           <TableHeader>
                              <TableRow className="dark:border-gray-700">
                                 <TableHead className="dark:text-gray-300">Application #</TableHead>
                                 <TableHead className="dark:text-gray-300">Type</TableHead>
                                 <TableHead className="dark:text-gray-300">Applicant</TableHead>
                                 <TableHead className="dark:text-gray-300">Contact</TableHead>
                                 <TableHead className="dark:text-gray-300">Expiry Date</TableHead>
                                 <TableHead className="dark:text-gray-300">Days Left</TableHead>
                                 <TableHead className="dark:text-gray-300">Status</TableHead>
                                 <TableHead className="dark:text-gray-300">Actions</TableHead>
                              </TableRow>
                           </TableHeader>
                           <TableBody>
                              {filteredPermits.map((permit) => {
                                 const expiryDate = permit.certificateDetails.expiryDate ?
                                    new Date(parseInt(permit.certificateDetails.expiryDate)) : null;
                                 const daysLeft = permit.certificateDetails.daysLeft;

                                 return (
                                    <TableRow
                                       key={permit.id}
                                       className={cn(
                                          getRowColorClass(daysLeft),
                                          "transition-colors dark:border-gray-700"
                                       )}
                                    >
                                       <TableCell className="dark:text-gray-300">{permit.applicationNumber}</TableCell>
                                       <TableCell className="dark:text-gray-300">{permit.applicationType}</TableCell>
                                       <TableCell className="dark:text-gray-300">{permit.applicantDetails.name}</TableCell>
                                       <TableCell className="dark:text-gray-300">
                                          {permit.applicantDetails.contact || 'N/A'}
                                       </TableCell>
                                       <TableCell className="dark:text-gray-300">
                                          {expiryDate ?
                                             format(expiryDate, 'MMMM d, yyyy') :
                                             'Invalid Date'
                                          }
                                       </TableCell>
                                       <TableCell>
                                          <Badge
                                             variant={getStatusBadgeVariant(daysLeft, permit.status)}
                                             className={cn(
                                                customBadgeVariants[getStatusBadgeVariant(daysLeft, permit.status)],
                                                "font-medium dark:text-gray-100"
                                             )}
                                          >
                                             {permit.status === 'Expired' ? 'Expired' : `${daysLeft} days`}
                                          </Badge>
                                       </TableCell>
                                       <TableCell className="dark:text-gray-300">{permit.status}</TableCell>
                                       <TableCell>
                                          <TooltipProvider delayDuration={200}>
                                             <Tooltip>
                                                <TooltipTrigger asChild>
                                                   <Button
                                                      variant="outline"
                                                      size="icon"
                                                      onClick={() => handleNotify(permit)}
                                                      className="h-8 w-8 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-700"
                                                   >
                                                      <Bell className="h-4 w-4" />
                                                   </Button>
                                                </TooltipTrigger>
                                                <TooltipContent className="dark:bg-gray-800 dark:text-gray-100">
                                                   <p>Send Notification</p>
                                                </TooltipContent>
                                             </Tooltip>
                                          </TooltipProvider>
                                       </TableCell>
                                    </TableRow>
                                 );
                              })}
                           </TableBody>
                        </Table>
                     )}
                  </div>
               </div>
            </div>
         </div>
      </div>
   );
};

export default ExpiringPermitsPage;
